/**
 * Employee Information API Client
 */

import apiClient from '@/core/api/client'
import type {
  OrgChartResponse,
  ProfileInfo,
  ProfileInfoInput,
  EmployeeDocument,
  DocumentInput,
  DocumentUpdateInput,
  DocumentListResponse,
  EmergencyContact,
  EmergencyContactInput,
  EmergencyContactUpdateInput,
  EmergencyContactListResponse,
  FamilyMember,
  FamilyMemberInput,
  FamilyMemberUpdateInput,
  FamilyMemberListResponse,
  EmployeeAddress,
  AddressInput,
  AddressUpdateInput,
  AddressListResponse,
  BankAccount,
  BankAccountInput,
  BankAccountUpdateInput,
  BankAccountListResponse,
  VerificationAction,
  EmployeeListResponse,
  VerificationQueueResponse,
} from '../types/employee-info.types'

const BASE_URL = '/v1/employee-info'

export const employeeInfoApi = {
  // ==================== Profile ====================

  getMyProfile: async () => {
    const response = await apiClient.get<ProfileInfo>(`${BASE_URL}/profile`)
    return response.data
  },

  updateMyProfile: async (data: ProfileInfoInput) => {
    const response = await apiClient.put<ProfileInfo>(`${BASE_URL}/profile`, data)
    return response.data
  },

  getEmployeeProfile: async (employeeId: string) => {
    const response = await apiClient.get<ProfileInfo>(`${BASE_URL}/profile/${employeeId}`)
    return response.data
  },

  verifyProfile: async (employeeId: string, data: VerificationAction) => {
    const response = await apiClient.post<ProfileInfo>(
      `${BASE_URL}/profile/${employeeId}/verify`,
      data
    )
    return response.data
  },

  // ==================== Documents ====================

  getMyDocuments: async (params?: {
    document_type?: string
    document_category?: string
    verification_status?: string
    page?: number
    limit?: number
  }) => {
    const response = await apiClient.get<DocumentListResponse>(`${BASE_URL}/documents`, { params })
    return response.data
  },

  uploadDocument: async (file: File, metadata: DocumentInput) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('document_type', metadata.document_type)
    formData.append('document_category', metadata.document_category)
    formData.append('title', metadata.title)
    if (metadata.description) formData.append('description', metadata.description)
    if (metadata.document_number) formData.append('document_number', metadata.document_number)
    if (metadata.issue_date) formData.append('issue_date', metadata.issue_date)
    if (metadata.expiry_date) formData.append('expiry_date', metadata.expiry_date)
    if (metadata.issuing_authority) formData.append('issuing_authority', metadata.issuing_authority)

    const response = await apiClient.post<EmployeeDocument>(`${BASE_URL}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  getDocument: async (documentId: string) => {
    const response = await apiClient.get<EmployeeDocument>(`${BASE_URL}/documents/${documentId}`)
    return response.data
  },

  updateDocument: async (documentId: string, data: DocumentUpdateInput) => {
    const response = await apiClient.put<EmployeeDocument>(
      `${BASE_URL}/documents/${documentId}`,
      data
    )
    return response.data
  },

  deleteDocument: async (documentId: string) => {
    await apiClient.delete(`${BASE_URL}/documents/${documentId}`)
  },

  downloadDocument: async (documentId: string) => {
    const response = await apiClient.get(`${BASE_URL}/documents/${documentId}/download`, {
      responseType: 'blob',
    })
    return response.data
  },

  verifyDocument: async (documentId: string, data: VerificationAction) => {
    const response = await apiClient.post<EmployeeDocument>(
      `${BASE_URL}/documents/${documentId}/verify`,
      data
    )
    return response.data
  },

  getEmployeeDocuments: async (
    employeeId: string,
    params?: {
      document_type?: string
      document_category?: string
      verification_status?: string
      page?: number
      limit?: number
    }
  ) => {
    const response = await apiClient.get<DocumentListResponse>(
      `${BASE_URL}/employees/${employeeId}/documents`,
      { params }
    )
    return response.data
  },

  // ==================== Emergency Contacts ====================

  getMyEmergencyContacts: async (params?: { page?: number; limit?: number }) => {
    const response = await apiClient.get<EmergencyContactListResponse>(
      `${BASE_URL}/emergency-contacts`,
      { params }
    )
    return response.data
  },

  createEmergencyContact: async (data: EmergencyContactInput) => {
    const response = await apiClient.post<EmergencyContact>(`${BASE_URL}/emergency-contacts`, data)
    return response.data
  },

  getEmergencyContact: async (contactId: string) => {
    const response = await apiClient.get<EmergencyContact>(
      `${BASE_URL}/emergency-contacts/${contactId}`
    )
    return response.data
  },

  updateEmergencyContact: async (contactId: string, data: EmergencyContactUpdateInput) => {
    const response = await apiClient.put<EmergencyContact>(
      `${BASE_URL}/emergency-contacts/${contactId}`,
      data
    )
    return response.data
  },

  deleteEmergencyContact: async (contactId: string) => {
    await apiClient.delete(`${BASE_URL}/emergency-contacts/${contactId}`)
  },

  verifyEmergencyContact: async (contactId: string, data: VerificationAction) => {
    const response = await apiClient.post<EmergencyContact>(
      `${BASE_URL}/emergency-contacts/${contactId}/verify`,
      data
    )
    return response.data
  },

  getEmployeeEmergencyContacts: async (
    employeeId: string,
    params?: { page?: number; limit?: number }
  ) => {
    const response = await apiClient.get<EmergencyContactListResponse>(
      `${BASE_URL}/employees/${employeeId}/emergency-contacts`,
      { params }
    )
    return response.data
  },

  // ==================== Family Members ====================

  getMyFamilyMembers: async (params?: { page?: number; limit?: number }) => {
    const response = await apiClient.get<FamilyMemberListResponse>(`${BASE_URL}/family-members`, {
      params,
    })
    return response.data
  },

  createFamilyMember: async (data: FamilyMemberInput) => {
    const response = await apiClient.post<FamilyMember>(`${BASE_URL}/family-members`, data)
    return response.data
  },

  getFamilyMember: async (memberId: string) => {
    const response = await apiClient.get<FamilyMember>(`${BASE_URL}/family-members/${memberId}`)
    return response.data
  },

  updateFamilyMember: async (memberId: string, data: FamilyMemberUpdateInput) => {
    const response = await apiClient.put<FamilyMember>(
      `${BASE_URL}/family-members/${memberId}`,
      data
    )
    return response.data
  },

  deleteFamilyMember: async (memberId: string) => {
    await apiClient.delete(`${BASE_URL}/family-members/${memberId}`)
  },

  verifyFamilyMember: async (memberId: string, data: VerificationAction) => {
    const response = await apiClient.post<FamilyMember>(
      `${BASE_URL}/family-members/${memberId}/verify`,
      data
    )
    return response.data
  },

  getEmployeeFamilyMembers: async (
    employeeId: string,
    params?: { page?: number; limit?: number }
  ) => {
    const response = await apiClient.get<FamilyMemberListResponse>(
      `${BASE_URL}/employees/${employeeId}/family-members`,
      { params }
    )
    return response.data
  },

  // ==================== Addresses ====================

  getMyAddresses: async (params?: { page?: number; limit?: number }) => {
    const response = await apiClient.get<AddressListResponse>(`${BASE_URL}/addresses`, { params })
    return response.data
  },

  createAddress: async (data: AddressInput) => {
    const response = await apiClient.post<EmployeeAddress>(`${BASE_URL}/addresses`, data)
    return response.data
  },

  getAddress: async (addressId: string) => {
    const response = await apiClient.get<EmployeeAddress>(`${BASE_URL}/addresses/${addressId}`)
    return response.data
  },

  updateAddress: async (addressId: string, data: AddressUpdateInput) => {
    const response = await apiClient.put<EmployeeAddress>(
      `${BASE_URL}/addresses/${addressId}`,
      data
    )
    return response.data
  },

  deleteAddress: async (addressId: string) => {
    await apiClient.delete(`${BASE_URL}/addresses/${addressId}`)
  },

  verifyAddress: async (addressId: string, data: VerificationAction) => {
    const response = await apiClient.post<EmployeeAddress>(
      `${BASE_URL}/addresses/${addressId}/verify`,
      data
    )
    return response.data
  },

  getEmployeeAddresses: async (employeeId: string, params?: { page?: number; limit?: number }) => {
    const response = await apiClient.get<AddressListResponse>(
      `${BASE_URL}/employees/${employeeId}/addresses`,
      { params }
    )
    return response.data
  },

  // ==================== Bank Accounts ====================

  getMyBankAccounts: async (params?: { page?: number; limit?: number }) => {
    const response = await apiClient.get<BankAccountListResponse>(`${BASE_URL}/bank-accounts`, {
      params,
    })
    return response.data
  },

  createBankAccount: async (data: BankAccountInput) => {
    const response = await apiClient.post<BankAccount>(`${BASE_URL}/bank-accounts`, data)
    return response.data
  },

  getBankAccount: async (accountId: string) => {
    const response = await apiClient.get<BankAccount>(`${BASE_URL}/bank-accounts/${accountId}`)
    return response.data
  },

  updateBankAccount: async (accountId: string, data: BankAccountUpdateInput) => {
    const response = await apiClient.put<BankAccount>(
      `${BASE_URL}/bank-accounts/${accountId}`,
      data
    )
    return response.data
  },

  deleteBankAccount: async (accountId: string) => {
    await apiClient.delete(`${BASE_URL}/bank-accounts/${accountId}`)
  },

  verifyBankAccount: async (accountId: string, data: VerificationAction) => {
    const response = await apiClient.post<BankAccount>(
      `${BASE_URL}/bank-accounts/${accountId}/verify`,
      data
    )
    return response.data
  },

  getEmployeeBankAccounts: async (
    employeeId: string,
    params?: { page?: number; limit?: number }
  ) => {
    const response = await apiClient.get<BankAccountListResponse>(
      `${BASE_URL}/employees/${employeeId}/bank-accounts`,
      { params }
    )
    return response.data
  },

  // ==================== Org Chart ====================

  getOrgChart: async () => {
    const response = await apiClient.get<OrgChartResponse>(`${BASE_URL}/org-chart`)
    return response.data
  },

  // ==================== HR Views ====================

  getAllEmployees: async (params?: {
    search?: string
    verification_status?: string
    page?: number
    limit?: number
  }) => {
    const response = await apiClient.get<EmployeeListResponse>(`${BASE_URL}/employees`, { params })
    return response.data
  },

  getVerificationQueue: async (params?: { page?: number; limit?: number }) => {
    const response = await apiClient.get<VerificationQueueResponse>(
      `${BASE_URL}/verification-queue`,
      { params }
    )
    return response.data
  },

  // ==================== HR: Create/Update on behalf of employee ====================

  updateEmployeeProfile: async (employeeId: string, data: ProfileInfoInput) => {
    const response = await apiClient.put<ProfileInfo>(`${BASE_URL}/profile/${employeeId}`, data)
    return response.data
  },

  createEmployeeEmergencyContact: async (employeeId: string, data: EmergencyContactInput) => {
    const response = await apiClient.post<EmergencyContact>(
      `${BASE_URL}/employees/${employeeId}/emergency-contacts`,
      data
    )
    return response.data
  },

  createEmployeeFamilyMember: async (employeeId: string, data: FamilyMemberInput) => {
    const response = await apiClient.post<FamilyMember>(
      `${BASE_URL}/employees/${employeeId}/family-members`,
      data
    )
    return response.data
  },

  createEmployeeAddress: async (employeeId: string, data: AddressInput) => {
    const response = await apiClient.post<EmployeeAddress>(
      `${BASE_URL}/employees/${employeeId}/addresses`,
      data
    )
    return response.data
  },

  createEmployeeBankAccount: async (employeeId: string, data: BankAccountInput) => {
    const response = await apiClient.post<BankAccount>(
      `${BASE_URL}/employees/${employeeId}/bank-accounts`,
      data
    )
    return response.data
  },
}
